"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Model = exports.UNDEFINED = void 0;
const tslib_1 = require("tslib");
const operations = tslib_1.__importStar(require("../../json-crdt-patch/operations"));
const clock = tslib_1.__importStar(require("../../json-crdt-patch/clock"));
const shared_1 = require("../codec/structural/binary/shared");
const api_1 = require("./api");
const constants_1 = require("../../json-crdt-patch/constants");
const util_1 = require("./util");
const nodes_1 = require("../nodes");
const ConNode_1 = require("../nodes/const/ConNode");
const printTree_1 = require("tree-dump/lib/printTree");
const Extensions_1 = require("../extensions/Extensions");
const AvlMap_1 = require("sonic-forest/lib/avl/AvlMap");
const json_crdt_patch_1 = require("../../json-crdt-patch");
const cmpNode_1 = require("../equal/cmpNode");
const print_1 = require("../../util/print");
const Delta_1 = require("../delta/Delta");
const Batch_1 = require("../../json-crdt-patch/Batch");
const Patch_1 = require("../../json-crdt-patch/Patch");
exports.UNDEFINED = new ConNode_1.ConNode(constants_1.ORIGIN, undefined);
/**
 * In instance of Model class represents the underlying data structure,
 * i.e. model, of the JSON CRDT document.
 */
class Model {
    /**
     * Use this method to generate a random session ID for an existing document.
     * It checks for the uniqueness of the session ID given the current peers in
     * the document. This reduces the chance of collision substantially.
     *
     * @returns A random session ID that is not used by any peer in the current
     *     document.
     */
    rndSid() {
        const clock = this.clock;
        const sid = clock.sid;
        const peers = clock.peers;
        while (true) {
            const candidate = (0, util_1.randomSessionId)();
            if (sid !== candidate && !peers.has(candidate))
                return candidate;
        }
    }
    /**
     * Instantiates a model from a collection of patches. The patches are applied
     * to the model in the order they are provided. The session ID of the model is
     * set to the session ID of the first patch.
     *
     * @param patches A collection of initial patches to apply to the model.
     * @returns A model with the patches applied.
     */
    static fromPatches(patches) {
        const length = patches.length;
        if (!length)
            throw new Error('NO_PATCHES');
        const first = patches[0];
        const sid = first.getId().sid;
        if (!sid)
            throw new Error('NO_SID');
        const model = Model.create(void 0, sid);
        model.applyBatch(patches);
        return model;
    }
    constructor(clockVector) {
        /**
         * Root of the JSON document is implemented as Last Write Wins Register,
         * so that the JSON document does not necessarily need to be an object. The
         * JSON document can be any JSON value.
         */
        this.root = new nodes_1.RootNode(this, constants_1.ORIGIN);
        /**
         * Index of all known node objects (objects, array, strings, values)
         * in this document.
         *
         * @ignore
         */
        this.index = new AvlMap_1.AvlMap(clock.compare);
        /**
         * Extensions to the JSON CRDT protocol. Extensions are used to implement
         * custom data types on top of the JSON CRDT protocol.
         *
         * @ignore
         * @todo Allow this to be `undefined`.
         */
        this.ext = new Extensions_1.Extensions();
        /**
         * Tracks number of times the `applyPatch` was called.
         *
         * @ignore
         */
        this.tick = 0;
        /**
         * Callback called before every `applyPatch` call.
         */
        this.onbeforepatch = undefined;
        /**
         * Callback called after every `applyPatch` call.
         */
        this.onpatch = undefined;
        /**
         * Callback called before model is reset using the `.reset()` method.
         */
        this.onbeforereset = undefined;
        /**
         * Callback called after model has been reset using the `.reset()` method.
         */
        this.onreset = undefined;
        this.clock = clockVector;
        if (!clockVector.time)
            clockVector.time = 1;
    }
    /**
     * API for applying local changes to the current document.
     */
    get api() {
        if (!this._api)
            this._api = new api_1.ModelApi(this);
        return this._api;
    }
    /**
     * Experimental node retrieval API using proxy objects. Returns a strictly
     * typed proxy wrapper around the value of the root node.
     */
    get s() {
        return this.api.s._;
    }
    /**
     * Experimental strictly typed node retrieval API using proxy objects.
     * Automatically resolves nested "val" nodes.
     */
    get $() {
        return this.api.$;
    }
    /**
     * Applies a delta to the model.
     *
     * @param delta Delta group - computed metadata difference between two models.
     */
    applyDelta(delta) {
        this.applyBatch(delta.batch.patches);
        this.clock.advanceCC(delta.vv1);
    }
    /**
     * Applies a batch of patches to the document.
     *
     * @param patches A batch, i.e. an array of patches.
     */
    applyBatch(patches) {
        const length = patches.length;
        for (let i = 0; i < length; i++)
            this.applyPatch(patches[i]);
    }
    /**
     * Works like `applyPatch`, but is intended to be used by the local client
     * for locally generated patches. It checks if the model clock is ahead of
     * the patch clock and rebases the patch if necessary.
     *
     * @param patch A patch to apply to the document.
     */
    applyLocalPatch(patch) {
        const id = patch.getId();
        if (id) {
            const clock = this.clock;
            if (clock.sid === id.sid) {
                const time = clock.time;
                if (time > id.time)
                    patch = patch.rebase(time);
            }
        }
        this.applyPatch(patch);
    }
    /**
     * Applies a single patch to the document. All mutations to the model must go
     * through this method. (With the only exception of local changes through API,
     * which have an alternative path.)
     *
     * @param patch A patch to apply to the document.
     */
    applyPatch(patch) {
        this.onbeforepatch?.(patch);
        const ops = patch.ops;
        const { length } = ops;
        for (let i = 0; i < length; i++)
            this.applyOperation(ops[i]);
        this.tick++;
        this.onpatch?.(patch);
    }
    /**
     * Applies a single operation to the model. All mutations to the model must go
     * through this method.
     *
     * For advanced use only, better use `applyPatch` instead. You MUST increment
     * the `tick` property and call the necessary event emitters manually.
     *
     * @param op Any JSON CRDT Patch operation
     * @ignore
     * @internal
     */
    applyOperation(op) {
        this.clock.observe(op.id, op.span());
        const index = this.index;
        if (op instanceof operations.InsStrOp) {
            const node = index.get(op.obj);
            if (node instanceof nodes_1.StrNode)
                node.ins(op.ref, op.id, op.data);
        }
        else if (op instanceof operations.NewObjOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new nodes_1.ObjNode(this, id));
        }
        else if (op instanceof operations.NewArrOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new nodes_1.ArrNode(this, id));
        }
        else if (op instanceof operations.NewStrOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new nodes_1.StrNode(id));
        }
        else if (op instanceof operations.NewValOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new nodes_1.ValNode(this, id, constants_1.ORIGIN));
        }
        else if (op instanceof operations.NewConOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new ConNode_1.ConNode(id, op.val));
        }
        else if (op instanceof operations.InsObjOp) {
            const node = index.get(op.obj);
            const tuples = op.data;
            const length = tuples.length;
            if (node instanceof nodes_1.ObjNode) {
                for (let i = 0; i < length; i++) {
                    const tuple = tuples[i];
                    const valueNode = index.get(tuple[1]);
                    if (!valueNode)
                        continue;
                    if (node.id.time >= tuple[1].time)
                        continue;
                    valueNode.parent = node;
                    const old = node.put(tuple[0] + '', valueNode.id);
                    if (old)
                        this._gcTree(old);
                }
            }
        }
        else if (op instanceof operations.InsVecOp) {
            const node = index.get(op.obj);
            const tuples = op.data;
            const length = tuples.length;
            if (node instanceof nodes_1.VecNode) {
                for (let i = 0; i < length; i++) {
                    const tuple = tuples[i];
                    const valueNode = index.get(tuple[1]);
                    if (!valueNode)
                        continue;
                    if (node.id.time >= tuple[1].time)
                        continue;
                    valueNode.parent = node;
                    const old = node.put(Number(tuple[0]), valueNode.id);
                    if (old)
                        this._gcTree(old);
                }
            }
        }
        else if (op instanceof operations.InsValOp) {
            const obj = op.obj;
            const node = obj.sid === 0 /* SESSION.SYSTEM */ && obj.time === 0 /* SYSTEM_SESSION_TIME.ORIGIN */ ? this.root : index.get(obj);
            if (node instanceof nodes_1.ValNode) {
                const newValue = index.get(op.val);
                if (newValue) {
                    newValue.parent = node;
                    const old = node.set(op.val);
                    if (old)
                        this._gcTree(old);
                }
            }
        }
        else if (op instanceof operations.InsArrOp) {
            const node = index.get(op.obj);
            if (node instanceof nodes_1.ArrNode) {
                const nodes = [];
                const data = op.data;
                const length = data.length;
                for (let i = 0; i < length; i++) {
                    const stamp = data[i];
                    const valueNode = index.get(stamp);
                    if (!valueNode)
                        continue;
                    if (node.id.time >= stamp.time)
                        continue;
                    nodes.push(stamp);
                    valueNode.parent = node;
                }
                if (nodes.length)
                    node.ins(op.ref, op.id, nodes);
            }
        }
        else if (op instanceof operations.UpdArrOp) {
            const node = index.get(op.obj);
            if (node instanceof nodes_1.ArrNode) {
                const val = op.val;
                const valueNode = index.get(val);
                if (valueNode) {
                    valueNode.parent = node;
                    const old = node.upd(op.ref, val);
                    if (old)
                        this._gcTree(old);
                }
            }
        }
        else if (op instanceof operations.DelOp) {
            const node = index.get(op.obj);
            if (node instanceof nodes_1.ArrNode) {
                const length = op.what.length;
                for (let i = 0; i < length; i++) {
                    const span = op.what[i];
                    for (let j = 0; j < span.span; j++) {
                        const id = node.getById(new clock.Timestamp(span.sid, span.time + j));
                        if (id)
                            this._gcTree(id);
                    }
                }
                node.delete(op.what);
            }
            else if (node instanceof nodes_1.StrNode)
                node.delete(op.what);
            else if (node instanceof nodes_1.BinNode)
                node.delete(op.what);
        }
        else if (op instanceof operations.NewBinOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new nodes_1.BinNode(id));
        }
        else if (op instanceof operations.InsBinOp) {
            const node = index.get(op.obj);
            if (node instanceof nodes_1.BinNode)
                node.ins(op.ref, op.id, op.data);
        }
        else if (op instanceof operations.NewVecOp) {
            const id = op.id;
            if (!index.get(id))
                index.set(id, new nodes_1.VecNode(this, id));
        }
    }
    /**
     * Recursively deletes a tree of nodes. Used when root node is overwritten or
     * when object contents of container node (object or array) is removed.
     *
     * @ignore
     */
    _gcTree(value) {
        const isSystemNode = value.sid === 0 /* SESSION.SYSTEM */;
        if (isSystemNode)
            return;
        const node = this.index.get(value);
        if (!node)
            return;
        node.parent = undefined;
        const api = node.api;
        if (api)
            api.events.handleDelete();
        node.children((child) => this._gcTree(child.id));
        this.index.del(value);
    }
    /**
     * Creates a copy of this model with a new session ID. If the session ID is
     * not provided, a random session ID is generated.
     *
     * This performs a deep clone of all model state without serialization,
     * which allows sharing of immutable data like strings, binary buffers, and IDs
     * between the original and the clone for memory efficiency.
     *
     * @param sessionId Session ID to use for the new model.
     * @returns A copy of this model with a new session ID.
     */
    fork(sessionId = this.rndSid()) {
        const clonedClock = this.clock instanceof clock.ClockVector
            ? this.clock.fork(sessionId)
            : this.clock.clone();
        const copy = new Model(clonedClock);
        copy.ext = this.ext.clone();
        const index = this.index;
        const copyIndex = copy.index;
        index.forEach(({ v: node }) => {
            let cloned;
            if (node instanceof ConNode_1.ConNode)
                cloned = node.clone();
            else if (node instanceof nodes_1.ValNode)
                cloned = node.clone(copy);
            else if (node instanceof nodes_1.ObjNode)
                cloned = node.clone(copy);
            else if (node instanceof nodes_1.VecNode)
                cloned = node.clone(copy);
            else if (node instanceof nodes_1.StrNode)
                cloned = node.clone();
            else if (node instanceof nodes_1.BinNode)
                cloned = node.clone();
            else if (node instanceof nodes_1.ArrNode)
                cloned = node.clone(copy);
            else
                throw new Error('UNKNOWN_NODE');
            copyIndex.set(cloned.id, cloned);
        });
        copy.root = this.root.clone(copy);
        copy.tick = this.tick;
        copy.linkParents();
        return copy;
    }
    /**
     * Creates a copy of this model with the same session ID.
     *
     * @returns A copy of this model with the same session ID.
     */
    clone() {
        return this.fork(this.clock.sid);
    }
    /**
     * Resets the model to equivalent state of another model.
     */
    reset(to) {
        this.onbeforereset?.();
        const oldIndex = this.index;
        this.index = new AvlMap_1.AvlMap(clock.compare);
        const blob = to.toBinary();
        shared_1.decoder.decode(blob, this);
        this.clock = to.clock.clone();
        this.ext = to.ext.clone();
        this.linkParents();
        const api = this._api;
        if (api) {
            api.flush();
            api.builder.clock = this.clock;
            api.node = this.root;
        }
        const changed = new Set();
        oldIndex.forEach(({ v: oldNode }) => {
            const nodeApi = oldNode.api;
            if (!nodeApi)
                return;
            const newNode = this.index.get(oldNode.id);
            if (!newNode) {
                nodeApi.events.handleDelete();
                return;
            }
            nodeApi.node = newNode;
            newNode.api = nodeApi;
            if (oldNode && newNode && !(0, cmpNode_1.cmpNode)(oldNode, newNode)) {
                changed.add(newNode);
            }
        });
        this.tick++;
        this.onreset?.(changed);
    }
    delta(vv) {
        const ops = [];
        this.root.delta(this, vv, ops);
        const batch = new Batch_1.Batch(ops.map((op) => new Patch_1.Patch([op])));
        return new Delta_1.Delta(vv, this.clock.clone(), batch);
    }
    merge(model) {
        const delta = model.delta(this.clock);
        this.applyDelta(delta);
    }
    /**
     * Returns the view of the model.
     *
     * @returns JSON/CBOR of the model.
     */
    view() {
        return this.root.view();
    }
    /**
     * Rebuilds `.parent` links for all nodes in the document.
     */
    linkParents() {
        const setParent = (parent, child) => {
            child.parent = parent;
            child.children((grandchild) => setParent(child, grandchild));
        };
        const root = this.root;
        root.parent = undefined;
        root.children((child) => setParent(root, child));
    }
    /**
     * Serialize this model using "binary" structural encoding.
     *
     * @returns This model encoded in octets.
     */
    toBinary() {
        return shared_1.encoder.encode(this);
    }
    /**
     * Strictly types the model and sets the default value of the model, if
     * the document is empty.
     *
     * @param schema The schema to set for this model.
     * @param sid Session ID to use for setting the default value of the document.
     *            Defaults to `SESSION.GLOBAL` (2), which is the default session ID
     *            for all operations operations that are not attributed to a specific
     *            session.
     * @returns Strictly typed model.
     */
    setSchema(schema, useGlobalSession = true) {
        const c = this.clock;
        const isNewDocument = c.time === 1;
        if (isNewDocument) {
            const oldSid = c.sid;
            if (useGlobalSession)
                c.sid = 2 /* SESSION.GLOBAL */;
            this.api.set(schema);
            if (useGlobalSession)
                this.setSid(oldSid);
        }
        return this;
    }
    /**
     * Changes the session ID of the model. By modifying the attached clock vector
     * of the model. Be careful when changing the session ID of the model, as this
     * is an advanced operation.
     *
     * Use the {@link Model.load} method to load a model with the the right session
     * ID, instead of changing the session ID of the model. When in doubt, use the
     * {@link Model.fork} method to create a new model with the right session ID.
     *
     * @param sid The new session ID to set for the model.
     */
    setSid(sid) {
        const cl = this.clock;
        const oldSid = cl.sid;
        if (oldSid !== sid) {
            cl.sid = sid;
            cl.observe(new clock.Timestamp(oldSid, cl.time - 1), 1);
        }
    }
    // ---------------------------------------------------------------- Printable
    toString(tab = '') {
        const nl = () => '';
        const hasExtensions = this.ext.size() > 0;
        return ('model' +
            (0, printTree_1.printTree)(tab, [
                (tab) => this.root.toString(tab),
                nl,
                (tab) => {
                    const nodes = [];
                    this.index.forEach((item) => nodes.push(item.v));
                    return (`index (${nodes.length} nodes)` +
                        (nodes.length
                            ? (0, printTree_1.printTree)(tab, nodes.map((node) => (tab) => `${node.name()} ${clock.printTs(node.id)}`))
                            : ''));
                },
                nl,
                (tab) => `view${(0, printTree_1.printTree)(tab, [(tab) => (0, print_1.indent)(tab, this.view())])}`,
                nl,
                (tab) => this.clock.toString(tab),
                hasExtensions ? nl : null,
                hasExtensions ? (tab) => this.ext.toString(tab) : null,
            ]));
    }
}
exports.Model = Model;
/**
 * Generates a random session ID. Use this method to generate a session ID
 * for a new user. Store the session ID in the user's browser or device once
 * and reuse it for all editing sessions of that user.
 *
 * Generating a new session ID for each editing session will work, however,
 * that is not recommended. If a user generates a new session ID for each
 * editing session, the session clock table will grow indefinitely.
 */
Model.sid = util_1.randomSessionId;
/**
 * Create a new JSON CRDT model. If a schema is provided, the model is
 * strictly typed and the default value of the model is set to the default
 * value of the schema.
 *
 * By default, the model is created with a random session ID and is using
 * a logical clock. It is also possible to create a model which uses a server
 * clock by providing the session ID `SESSION.SERVER` (1).
 *
 * ### Examples
 *
 * Create a basic model, without schema and default value:
 *
 * ```ts
 * const model = Model.create();
 * ```
 *
 * Create a strictly typed model with a schema and default value:
 *
 * ```ts
 * const schema = s.obj({
 *   ticker: s.con<string>('BODEN'),
 *   name: s.str('Jeo Boden'),
 *   tags: s.arr(
 *     s.str('token'),
 *   ),
 * });
 * const model = Model.create(schema);
 * const patch = model.api.flush();
 * ```
 *
 * Create a model with a custom session ID for your logical clock:
 *
 * ```ts
 * const schema = s.str('');
 * const sid = 123456789;
 * const model = Model.create(schema, sid);
 * const patch = model.api.flush();
 * ```
 *
 * The session ID must be at least 65,536 or higher, [see JSON CRDT Patch
 * specification][json-crdt-patch].
 *
 * [json-crdt-patch]: https://jsonjoy.com/specs/json-crdt-patch/patch-document/logical-clock
 *
 * To create a model with a server clock, use the `SESSION.SERVER`, which is
 * equal to 1:
 *
 * ```ts
 * const model = Model.create(undefined, SESSION.SERVER);
 * // or
 * const model = Model.create(undefined, 1);
 * ```
 *
 * Finally, you can create a model with your clock vector:
 *
 * ```ts
 * const clock = new ClockVector(123456789, 1);
 * const model = Model.create(undefined, clock);
 * ```
 *
 * @param schema The schema (typing and default value) to set for this model.
 *     When a schema is provided, the model is strictly typed and the default
 *     value of the model is set to the value of the schema. Also, you MUST
 *     call `model.api.flush()` immediately after creating the model to clear
 *     the change buffer of the patch that was created during the initialization
 *     of the model.
 * @param sidOrClock Session ID to use for local operations. Defaults to a random
 *        session ID generated by {@link Model.sid}.
 * @returns A strictly typed model.
 */
Model.create = (schema, sidOrClock = Model.sid()) => {
    const cl = typeof sidOrClock === 'number'
        ? sidOrClock === 1 /* SESSION.SERVER */
            ? new clock.ServerClockVector(1 /* SESSION.SERVER */, 1)
            : new clock.ClockVector(sidOrClock, 1)
        : sidOrClock;
    const model = new Model(cl);
    if (schema !== void 0)
        model.setSchema(schema instanceof json_crdt_patch_1.NodeBuilder ? schema : json_crdt_patch_1.s.json(schema), true);
    return model;
};
/**
 * Create a CRDT model which uses server clock. In this model a central server
 * timestamps each operation with a sequence number. Each timestamp consists
 * simply of a sequence number, which was assigned by a server. In this model
 * all operations are approved, persisted and re-distributed to all clients by
 * a central server. The session ID part of the clock is the same for all
 * clients, i.e. {@link SESSION.SERVER}.
 *
 * @param time Latest known server sequence number.
 * @returns CRDT model.
 */
Model.withServerClock = (schema, time = 1) => Model.create(schema, new clock.ServerClockVector(1 /* SESSION.SERVER */, time));
/**
 * Decodes a model from a "binary" structural encoding.
 *
 * Use {@link Model.load} instead, if you want to set the session ID of the
 * model and the right schema for the model, during the de-serialization.
 *
 * @param data Binary blob of a model encoded using "binary" structural
 *        encoding.
 * @returns An instance of a model.
 */
Model.fromBinary = (data) => {
    return shared_1.decoder.decode(data);
};
/**
 * Un-serializes a model from "binary" structural encoding. The session ID of
 * the model is set to the provided session ID `sid`, or the default session
 * ID of the un-serialized model is used.
 *
 * @param data Binary blob of a model encoded using "binary" structural
 *        encoding.
 * @param sid Session ID to set for the model.
 * @returns An instance of a model.
 */
Model.load = (data, sid, schema) => {
    const model = shared_1.decoder.decode(data);
    if (schema)
        model.setSchema(schema, true);
    if (typeof sid === 'number')
        model.setSid(sid);
    return model;
};
